import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ShipmentService} from '@services/shipment.service';
import {Shipment, ShipmentForOrder} from '@models/shipment.interface';
import {
  AppState,
  selectDefaultInsurance,
  selectFooterAction,
  selectOrderCompletion,
  selectOrderCreation,
  selectUserWithCountryCode
} from '@store/reducers';
import {Store} from '@ngrx/store';
import {DeleteAllShipmentsInCart, ORDER_CREATION_CLEAR, ORDER_SUMMARY_CLEAR, OrderIsLoading} from '@store/actions';
import {Subject} from 'rxjs/Subject';
import {takeUntil} from 'rxjs/operators';
import {AdyenService} from '@services/adyen.service';
import {OrderCompletionState} from '@store/reducers/order-completion.reducer';
import {UserWithCountryCode} from '@models/user.interface';
import {ShipmentUpdate} from '@store/actions/order-creation.action';
import {OrderCreationState} from '@store/reducers/order-creation.reducer';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {FooterActionEnum} from '@models/footer-action.enum';
import 'rxjs/add/operator/map';

@Component({
  selector: 'app-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss']
})
export class OrderSummaryComponent implements OnInit, OnDestroy {
  private unsubscribe: Subject<void> = new Subject();
  private possibleCountries: Array<{ value: string; label: string }>;
  public shipments: Shipment[];
  public prices: { [shipmentId: string]: {
      shipmentAmount: number;
      shipmentVat: number;
      additionalServicesAmount: number;
      additionalServicesVat: number;
    } } = {};

  public totals: {
    subtotal: number;
    invoiceFee: number;
    vat: number;
  } = { subtotal: 0, invoiceFee: 0, vat: 0 };

  public billingAddress: any = null;
  public orderCompletion: OrderCompletionState;

  constructor(
    private shipmentService: ShipmentService,
    private store: Store<AppState>,
    private adyenService: AdyenService,
    private translate: TranslateService,
    private router: Router,
  ) {
  }

  ngOnInit() {
    this.store.select(selectOrderCreation)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((state: OrderCreationState) => {
        this.shipments = Object.keys(state.shipments).map(id => {
          const shipment = {id, ...state.shipments[id]} as Shipment;

          // Check if service is required
          if (shipment.details && shipment.details.additional_services) {
            Object.keys(shipment.details.additional_services).forEach(serviceCode => {
              const service = shipment.details.additional_services[serviceCode];
              if (service.required) {
                if (!shipment.selected_additional_services) {
                  shipment.selected_additional_services = {};
                }
                shipment.selected_additional_services[serviceCode] = { selected: true };
              }
            });
          }

          return shipment;
        }).sort((b, a) => this.sortFn('search_id', a.details, b.details));
        // console.log('order-summary', this.shipments);

        // this.handleAdditionalServiceEvent()
        this.calculatePrice();
      });

    this.shipmentService.getInitialData().switchMap(() => {
      this.possibleCountries = this.shipmentService.possibleCountries;

      return this.store.select(selectUserWithCountryCode);
    })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data: UserWithCountryCode) => {
        const {
          company_name, register_num, name, surname, email, phone,
          is_company, invoice_address, invoice_zip, invoice_city, invoice_country_code
        } = data;
        const country = this.possibleCountries.find(country => country.value === invoice_country_code);
        this.billingAddress = {
          company_name,
          reg_number: register_num,
          name,
          surname,
          email,
          phone,
          type: is_company ? 'company' : 'private',
          address: invoice_address,
          zip: invoice_zip,
          city: invoice_city,
          country_code: invoice_country_code || country.value,
          country: country.label,
        };
      });

    this.store.select(selectOrderCompletion)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        this.orderCompletion = data;
      });

    this.store.select(selectFooterAction)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(action => {
        if (action === FooterActionEnum.GO_TO_CREATE_CONSIGNMENT_SEARCH_RESULT) {
          this.router.navigate(['create-consignment', 'search-result']);
        }
      });

    /*
    this.store.select(selectDefaultInsurance)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        const insuranceDefault = Boolean(Number(data));
        this.handleTotalInsuranceChange(insuranceDefault);
      });
      */
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.store.dispatch(new DeleteAllShipmentsInCart());
  }

  sortFn = (parameter, a, b) => a[parameter] - b[parameter];

  calculatePrice() {
    let totalSubtotal = 0;
    let totalInvoiceFee = 0; // You can customize this later if needed
    let totalVat = 0;

    this.prices = {};

    this.shipments.forEach((shipment: ShipmentForOrder) => {
      const { charge, vat, additional_services } = shipment.details;

      const additionalServicesAmount = this.calculateAdditionalServicesAmount(
        shipment.selected_additional_services,
        additional_services
      );

      const shipmentAmount = parseFloat(charge);
      const shipmentVat = parseFloat(vat);
      const additionalServicesVat = additionalServicesAmount.vatAmount;
      const subtotal = shipmentAmount + additionalServicesAmount.serviceAmount;
      const totalVatForShipment = shipmentVat + additionalServicesVat;

      totalSubtotal += subtotal;
      totalVat += totalVatForShipment;

      this.prices[shipment.id] = {
        shipmentAmount,
        shipmentVat,
        additionalServicesAmount: additionalServicesAmount.serviceAmount,
        additionalServicesVat,
      };

      // Update total_charge for the shipment with the correct id
      const shipmentToUpdate = this.shipments.find(s => s.id === shipment.id);
      if (shipmentToUpdate) {
        // shipmentToUpdate.details.charge = shipmentAmount;
        // shipmentToUpdate.details.vat = shipmentVat;
        shipmentToUpdate.details.total_charge = subtotal.toString();
        // console.log('shipmentToUpdate', shipmentToUpdate);
      }
    });

    this.totals.subtotal = parseFloat(totalSubtotal.toFixed(2));
    this.totals.invoiceFee = totalInvoiceFee;
    this.totals.vat = parseFloat(totalVat.toFixed(2));

    // console.log('calculatePrice', {
    //   prices: this.prices,
    //   totals: this.totals
    // });
  }

  calculateAdditionalServicesAmount(selectedAdditionalServices, availableAdditionalServices): {serviceAmount: number, vatAmount: number} {
    // console.log('calculateAdditionalServicesAmount', selectedAdditionalServices, availableAdditionalServices);
    // return;

    if (!selectedAdditionalServices || !availableAdditionalServices) {
      return { serviceAmount: 0, vatAmount: 0 };
    }

    return Object.keys(selectedAdditionalServices).reduce((acc, serviceCode) => {
      const service = selectedAdditionalServices[serviceCode];
      if (service.selected && availableAdditionalServices.hasOwnProperty(serviceCode)) {
        const amount = parseFloat(availableAdditionalServices[serviceCode].price.amount);
        const vatRate = availableAdditionalServices[serviceCode].price.vat_rate || 0;
        acc.serviceAmount += amount;
        acc.vatAmount += amount * (vatRate / 100);
      }
      return acc;
    }, { serviceAmount: 0, vatAmount: 0 });
  }

  // insurance
  // get isInsuredTotally() {
  //   return this.shipments.every((shipment: ShipmentForOrder): boolean => !!shipment.addons);
  // }

  // handleTotalInsuranceChange(value) {
  //   this.shipments.forEach(shipment => this.handleShipmentUpdate(value, shipment.id));
  // }

  handleAdditionalServiceEvent(event, searchResultId) {
    console.log('handleAdditionalServiceEvent', event);
    const {serviceCode, isActive, formFields} = event;
    // Access formFields data here
    // console.log('Form Fields:', formFields);
    // console.log(`Service Code: ${serviceCode}, Status: ${status}, Shipment ID: ${searchResultId}`);
    let shipment: any = this.shipments.find(({ id }) => id === searchResultId);
    // console.log('Shipment:', shipment);

    if (!shipment.selected_additional_services) {
      shipment.selected_additional_services = {};
    }

    if (formFields[serviceCode].selected) {
      const data = formFields[serviceCode].data;
      shipment.selected_additional_services[serviceCode] = {
        selected: true,
        ...(Object.keys(data).length > 0 && { data })
      };
    } else {
      delete shipment.selected_additional_services[serviceCode];
    }

    this.calculatePrice();

    shipment = {...shipment};
    shipment = {
      ...shipment,
      details: {
        ...shipment.details,
        charge: this.prices[shipment.id].shipmentAmount,
        vat : this.prices[shipment.id].shipmentVat,
        // additional_services_charge: this.price.additionalServicesAmount,
        // additional_services_vat: this.price.additionalServicesVat,
        total_charge: this.prices[shipment.id].shipmentAmount + this.prices[shipment.id].additionalServicesAmount,
      }
    };

   // console.log(
   //   shipment.details.charge,
   //   shipment.details.vat,
   //   // shipment.details.additional_services_charge,
   //   // shipment.details.additional_services_vat,
   //   shipment.details.total_charge
   // );
    // console.log('handleAdditionalServiceEvent', {total_charge: shipment.details.total_charge, subtotal: this.price.subtotal, vat: this.price.vat});

    this.store.dispatch(new ShipmentUpdate({shipment}));
  }

  /*
  handleShipmentUpdate(value, index) {
    // console.log(value, index);
    let shipment: any = this.shipments.find(shipment => shipment.id === index);
    shipment = {...shipment};
    if (value) {
      shipment = {
        ...shipment,
        details: {
          ...shipment.details,
          total_charge: shipment.details.charge + +shipment.details.addons.insurance.price
        },
        addons: {
          insurance: {
            id: shipment.details.addons.insurance.info.id
          },
        }
      };
    } else {
      delete shipment.selected_additional_services;
      shipment = {
        ...shipment,
        details: {
          ...shipment.details,
          total_charge: shipment.details.charge
        }
      };
    }

    console.log('handleShipmentUpdate', shipment, value, index);

    this.store.dispatch(new ShipmentUpdate({shipment}));
  }
  */

  // order
  handleOrder(paymentData) {
    this.store.dispatch(new OrderIsLoading({parameters: this.prepareOrderData(paymentData)}));
    this.router.navigate(['create-consignment', 'order-complete']);
  }

  prepareOrderData(paymentData: any): any {
    let orderData: any = {
      shipments: this.shipments.map(shipment => {
        delete shipment.id;
        const {id, details, ...restShipment} = shipment;
        // const {addons, ...restShipmentDetails} = details;

        return {
          ...restShipment,
          details: details
        };
      }),
      billing_address: this.billingAddress,
      payment_service: paymentData.payment_service,

      currency: 'SEK',
      create_account: false,
      send_freight_documents_to_payer: false,
      language: this.translate.currentLang
    };
    if (this.orderCompletion.order_id) {
      orderData.order_id = this.orderCompletion.order_id;
    }
    // card payment method
    if (paymentData.payment_service === '1') {
      orderData = {
        ...orderData,
        encrypted_data: paymentData.card && this.adyenService.getEncryptedData(paymentData.card),
        payment_method: paymentData.card && this.adyenService.creditCardType,
        payment_card: paymentData.card && paymentData.card.payment_card.slice(-4),
      };
    } else if (paymentData.payment_service === '2') {
      // invoice payment method
      const isCompany = paymentData.invoice && paymentData.invoice.is_payer_company;
      orderData = {
        ...orderData,
        identity_number: paymentData.invoice && paymentData.invoice.identity_number,
        order_reference: isCompany ? paymentData.invoice && paymentData.invoice.order_reference : '',
        organisation: isCompany ? paymentData.invoice && paymentData.invoice.organisation : '',
        is_payer_company: paymentData.invoice && paymentData.invoice.is_payer_company,
      };
    }

    // console.log('prepareOrderData', orderData);

    return orderData;
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/consignment/order-summary/order-summary.component.ts